import { useLocation, useMatches } from "@remix-run/react";
import { useEffect } from "react";
import { trackScreen } from "~/lib/journey-tracker.client";

/** Names for the screens the journey map follows; other routes use their route id. */
const SCREENS: Record<string, string> = {
  "routes/_app.landing-pub": "discover",
  "routes/_app.messages": "messages_list",
  "routes/_app.messages.$id": "chat",
  "routes/_app.user-profile.$id": "persona_profile",
  "routes/_app.my-profile": "my_profile",
  "routes/_app.settings": "settings",
  "routes/_app.search": "search",
  "routes/_app.verify-email": "verify_email",
  "routes/_index": "home",
  "routes/process-payment": "payment_result",
  "routes/auth.login": "login",
  "routes/auth.register": "register",
  "routes/auth.forgot-password": "forgot_password",
  "routes/auth.oauth.google": "google_signin",
};

function screenFor(routeId: string): string {
  if (SCREENS[routeId]) return SCREENS[routeId];
  if (routeId.startsWith("routes/lp.")) return "landing_page";
  return routeId.replace(/^routes\//, "").replace(/[^A-Za-z0-9_.:-]/g, "_").slice(0, 64) || "unknown";
}

/**
 * Records a screen_view each time the customer lands on another screen
 * (lib/journey-tracker.client.ts). Keyed on the path, the route and its
 * `?view=` panel, not on the whole URL: search and chat pagination rewrite
 * the query string without changing the screen.
 */
export function useScreenTracking() {
  const matches = useMatches();
  const location = useLocation();
  const leaf = matches[matches.length - 1];
  const routeId = leaf?.id ?? "";
  const params = leaf?.params ?? {};
  const search = new URLSearchParams(location.search);
  const view = search.get("view") ?? undefined;
  const status = routeId === "routes/process-payment" ? search.get("status") ?? undefined : undefined;
  const key = [location.pathname, routeId, view ?? "", status ?? ""].join("|");

  useEffect(() => {
    if (!routeId) return;
    const screen = screenFor(routeId);
    const props: Record<string, string | undefined> = { view, status };
    if (screen === "chat") props.conversation_id = params.id;
    if (screen === "persona_profile") props.persona_id = params.id;
    if (screen === "landing_page") {
      props.page = routeId.replace(/^routes\/lp\./, "").replace(/\.\(\$variant\)$/, "");
      props.variant = params.variant;
    }
    trackScreen(screen, props);
    // One event per screen change: `key` captures everything that defines one.
  }, [key]);
}
